import { Link } from "react-router-dom";
import { DISCLAIMER, SITE } from "@/constants";
import FacebookIcon from "@/components/icons/facebook-icon";
import InstagramIcon from "@/components/icons/instagram-icon";
import LinkedInIcon from "@/components/icons/linkedin-icon";
import TikTokIcon from "@/components/icons/tiktok-icon";
import XIcon from "@/components/icons/x-icon";
import YoutubeIcon from "@/components/icons/youtube-icon";
import { NewBadge, isStillNew } from "@/components/ui/new-badge";

/**
 * Footer component that displays the Lexi.tips logo, navigation links, and copyright information
 * Matches the minimal design shown in the image
 */
function Footer() {
  const isHomePage = window.location.pathname === "/";

  const scrollToSection = (href) => {
    // If it's not an anchor link, use regular navigation
    if (!href.includes("#")) {
      window.location.href = href;
      return;
    }

    // If we're not on the home page, navigate to home first
    if (!isHomePage) {
      const hash = href.split("#")[1];
      sessionStorage.setItem("scrollTarget", hash);
      window.location.href = "/";
      return;
    }

    // Handle anchor scrolling when on home page
    const id = href.split("#")[1];
    const element = document.getElementById(id);
    if (element) {
      // Add small delay to ensure consistent behavior across devices
      setTimeout(() => {
        requestAnimationFrame(() => {
          const navbarHeight = document.querySelector("nav").offsetHeight;
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: "smooth",
          });
        });
      }, 100);
    }
  };

  const navLinks = [
    {
      label: "How it works",
      href: isHomePage ? "#howitworks" : "/#howitworks",
    },
    { label: "Meet Lexi", href: isHomePage ? "#meetlexi" : "/#meetlexi" },
    { label: "Features", href: isHomePage ? "#features" : "/#features" },
    { label: "Results", href: "/race-results", newUntil: "2026-12-01" },
    { label: "Pricing", href: isHomePage ? "#pricing" : "/#pricing" },
    { label: "Contact", href: isHomePage ? "#contact" : "/#contact" },
  ];

  const socialLinks = [
    // {
    //   label: "LinkedIn",
    //   icon: <LinkedInIcon className="w-5 h-5 text-white" />,
    //   href: "#",
    // },
    // {
    //   label: "Facebook",
    //   icon: <FacebookIcon className="w-5 h-5 text-white" />,
    //   href: "#",
    // },
    // {
    //   label: "YouTube",
    //   icon: <YoutubeIcon className="w-5 h-5 text-white" />,
    //   href: "#",
    // },
    {
      label: "Instagram",
      icon: <InstagramIcon className="w-5 h-5 text-white" />,
      href: "https://www.instagram.com/lexi.tips/",
    },
    {
      label: "X/Twitter",
      icon: <XIcon className="w-5 h-5 text-white" />,
      href: "https://x.com/Lexi_Tips",
    },
    {
      label: "Facebook",
      icon: <FacebookIcon className="w-5 h-5 text-white" />,
      href: "https://www.facebook.com/LexiDotTips",
    },
  ];

  return (
    <footer className="bg-pink-200 py-12">
      <div className="container mx-auto px-4 flex flex-col items-center">
        {/* Logo */}
        <h2 className="text-4xl font-bold italic mb-8">{SITE.name}</h2>

        {/* Navigation */}
        <nav className="flex flex-wrap justify-center gap-8 mb-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href);
              }}
              className="inline-flex items-center gap-1.5 text-gray-700 hover:text-black transition-colors"
            >
              {link.label}
              {isStillNew(link.newUntil) && (
                <NewBadge className="border-white bg-white" />
              )}
            </a>
          ))}
        </nav>

        {/*Disclaimer */}
        <div className="max-w-2xl mx-auto text-center text-gray-700 text-sm mb-8 p-4 rounded-lg">
          <p dangerouslySetInnerHTML={{ __html: DISCLAIMER.disclaimer }} />
        </div>

        {/* Copyright and Legal */}
        <div className="text-center text-gray-700 text-sm">
          <p className="mb-2">© {new Date().getFullYear()} Lexi.tips. All rights reserved.</p>
          <div className="flex justify-center gap-2">
            <a
              href="/subscription-agreement"
              className="hover:text-black transition-colors"
            >
              Subscription Agreement
            </a>
            <span>•</span>
            <Link to="/privacy" className="hover:text-black transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>

        {/* Social Media Icons */}
        <div className="flex justify-center gap-4 mt-8">
          {socialLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              aria-label={link.label}
              className="bg-transparent p-2 rounded-md border-2 border-gray-400 hover:bg-white hover:text-white transition-colors"
              target="_blank"
            >
              {link.icon}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default Footer;
